import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Grid,
  IconButton,
  MenuItem,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/styles";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  BCPCalendarDay,
  BCPChartMixed,
  BCPDiscord,
  BCPEllipsisVertical,
  BCPEnvelope,
  BCPFacebook,
  BCPInstagram,
  BCPLinkSimple,
  BCPLocationDot,
  BCPPhone,
  BCPPlus,
  BCPSearch,
  BCPTwitch,
  BCPTwitterX,
  BCPYoutube
} from "../../../../components/icons/bcp/BCPIcons";
import EventCard from "../../../../components/layout/EventCard";
import InfoContainer from "../../../../components/layout/InfoContainer";
import ListResponsive from "../../../../components/other/ListResponsive";
import {
  getData,
  titleCase
} from "../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { userDisplayName } from "../../../events/components/EventFunctions";
import { ContactOrganizationModal } from "../../OrganizationLanding";
import { AuthContext } from "../../../../components/user/BCPAuthProvider";
import StyledMenu from "../../../../components/layout/StyledMenu";
import ExportModal from "../../../../components/modals/ExportModal";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import ExternalLink from "../../../../components/utilities/ExternalLink";
import { findGetParameter } from "../../../../components/utilities/UtilityFunctions";
import { useEmbeddedExitConfirm } from "../../../../hooks/useEmbeddedExitConfirm";
import EmbeddedExitConfirmDialog from "../../../../components/modals/EmbeddedExitConfirmDialog";
import ExternalCardActionArea from "../../../../components/utilities/ExternalCardActionArea";

export default function OrgDashOverview(props) {
  const {
    upcomingEvents,
    setUpcomingEvents,
    organization,
    organize,
    leaderboardPreview,
    setTab,
    authContext,
    loading
  } = props;

  const deleteEvent = (eventId) => {
    var eventArray = [];
    if (upcomingEvents?.length) {
      upcomingEvents.forEach((event) => {
        if (event.id !== eventId) {
          eventArray.push(event);
        }
      });
    }
    setUpcomingEvents(eventArray);
  };

  const [contactOrganizationModal, setContactOrganizationModal] =
    useState(false);

  const theme = useTheme();
  const navigate = useNavigate();

  const embed = findGetParameter("embed") === "true";

  const {
    interceptExternalLink,
    dialogOpen,
    handleConfirm,
    handleCancel,
    brandName,
    storagePrefix
  } = useEmbeddedExitConfirm();

  const cardSx = {
    p: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start"
  };
  const iconSx = {
    color: theme?.palette?.text?.secondary,
    "vertical-align": "bottom"
  };

  return (
    <Grid container spacing={1}>
      {/* Card section */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={cardSx}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Details
            </Typography>

            {Boolean(
              organization?.location?.city ||
                organization?.location?.zip ||
                organization?.location?.state ||
                organization?.location?.country
            ) && (
              <Typography
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Box
                  sx={{
                    minWidth: "35px",
                    verticalAlign: "bottom",
                    display: "inline-flex"
                  }}
                >
                  <Tooltip title="Event Location">
                    <BCPLocationDot
                      style={{
                        color: theme?.palette?.text?.secondary,
                        "vertical-align": "bottom"
                      }}
                    />
                  </Tooltip>
                </Box>
                <ExternalLink
                  underline="none"
                  color="link.main"
                  href={`https://google.com/maps/place/${
                    organization?.location?.streetNum || ""
                  } ${organization?.location?.streetName || ""} ${
                    organization?.location?.city || ""
                  } ${organization?.location?.zip || ""} ${
                    organization?.location?.state || ""
                  } ${organization?.location?.country || ""}`.trim()}
                  target="_blank"
                >
                  {Boolean(
                    organization?.location?.streetNum ||
                      organization?.location?.streetName
                  ) && (
                    <Typography>
                      {`${organization?.location?.streetNum || ""} ${
                        organization?.location?.streetName || ""
                      }`.trim()}
                    </Typography>
                  )}
                  {Boolean(
                    organization?.location?.city ||
                      organization?.location?.zip ||
                      organization?.location?.state
                  ) && (
                    <Typography>
                      {`${
                        organization?.location?.city
                          ? `${organization?.location?.city}, `
                          : ""
                      }${organization?.location?.state || ""} ${
                        organization?.location?.zip || ""
                      }`.trim()}
                    </Typography>
                  )}
                  {Boolean(organization?.location?.country) && (
                    <Typography>{organization?.location?.country}</Typography>
                  )}
                </ExternalLink>
              </Typography>
            )}

            {organization?.websiteURL && (
              <Typography gutterBottom>
                <Box
                  sx={{
                    minWidth: "35px",
                    verticalAlign: "bottom",
                    display: "inline-flex"
                  }}
                >
                  <Tooltip title="Organization Website">
                    <BCPLinkSimple
                      style={{
                        color: theme?.palette?.text?.secondary,
                        "vertical-align": "bottom"
                      }}
                    />
                  </Tooltip>
                </Box>
                {
                  <ExternalLink
                    underline="none"
                    color="link.main"
                    target="_blank"
                    href={organization?.websiteURL}
                  >
                    View Website
                  </ExternalLink>
                }
              </Typography>
            )}

            {(organization?.phoneNumber ||
              organization?.onboarding?.phoneNumber) && (
              <Typography gutterBottom>
                <Box
                  sx={{
                    minWidth: "35px",
                    verticalAlign: "bottom",
                    display: "inline-flex"
                  }}
                >
                  <Tooltip title="Phone Number">
                    <BCPPhone
                      style={{
                        color: theme?.palette?.text?.secondary,
                        "vertical-align": "bottom"
                      }}
                    />
                  </Tooltip>
                </Box>
                <ExternalLink
                  underline="none"
                  color="link.main"
                  target="_blank"
                  href={`tel:${
                    organization?.phoneNumber ||
                    organization?.onboarding?.phoneNumber
                  }`}
                >
                  {organization?.phoneNumber ||
                    organization?.onboarding?.phoneNumber}
                </ExternalLink>
              </Typography>
            )}

            <Typography gutterBottom>
              <Box
                sx={{
                  minWidth: "35px",
                  verticalAlign: "bottom",
                  display: "inline-flex"
                }}
              >
                <Tooltip title="Email">
                  <BCPEnvelope
                    style={{
                      color: theme?.palette?.text?.secondary,
                      "vertical-align": "bottom"
                    }}
                  />
                </Tooltip>
              </Box>
              <ExternalLink
                underline="none"
                color="link.main"
                href="#"
                onClick={(e) => {
                  setContactOrganizationModal(true);
                }}
              >
                {organization?.storeEmail ||
                  organization?.onboarding?.storeEmail ||
                  "Contact Organization"}
              </ExternalLink>
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={cardSx}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Socials
            </Typography>

            {organization?.socials &&
            Object.keys(organization?.socials).length ? (
              <>
                {Object.keys(organization?.socials).map((social, index) => (
                  <Typography gutterBottom key={index}>
                    <Box
                      sx={{
                        minWidth: "35px",
                        verticalAlign: "bottom",
                        display: "inline-flex"
                      }}
                    >
                      <Tooltip title={social}>
                        {social?.toLowerCase() === "facebook" ? (
                          <BCPFacebook sx={iconSx} />
                        ) : social?.toLowerCase() === "instagram" ? (
                          <BCPInstagram sx={iconSx} />
                        ) : social?.toLowerCase() === "twitter" ? (
                          <BCPTwitterX sx={iconSx} />
                        ) : social?.toLowerCase() === "twitch" ? (
                          <BCPTwitch sx={iconSx} />
                        ) : social?.toLowerCase() === "youtube" ? (
                          <BCPYoutube sx={iconSx} />
                        ) : social?.toLowerCase() === "discord" ? (
                          <BCPDiscord sx={iconSx} />
                        ) : (
                          <BCPLinkSimple sx={iconSx} />
                        )}
                      </Tooltip>
                    </Box>
                    <ExternalLink
                      underline="none"
                      color="link.main"
                      target="_blank"
                      href={organization?.socials[social]}
                    >
                      {social}
                    </ExternalLink>
                  </Typography>
                ))}
              </>
            ) : (
              <Typography gutterBottom>No socials found</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      {/* <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={cardSx}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Games
            </Typography>
            {organization?.gameSystems ? (
              organization.gameSystems.map((gs, index) => (
                <Typography key={gs.id} gutterBottom>
                  {gs.name}
                </Typography>
              ))
            ) : (
              <Typography gutterBottom>No games found</Typography>
            )}
          </CardContent>
        </Card>
      </Grid> */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={cardSx}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Hours
            </Typography>

            {organization?.openingHours ? (
              Object.keys(organization?.openingHours)?.map((day, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%"
                  }}
                >
                  <Typography gutterBottom>{titleCase(day)}</Typography>
                  <Typography gutterBottom>
                    {organization.openingHours[day]?.hours?.length
                      ? `${moment(
                          organization.openingHours[day]?.hours[0]["open"],
                          "HH:mm"
                        ).format("LT")} - ${moment(
                          organization.openingHours[day]?.hours[0]["close"],
                          "HH:mm"
                        ).format("LT")}`.trim()
                      : "Closed"}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography gutterBottom>
                No specified hours of operation
              </Typography>
            )}
            <Typography gutterBottom></Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Events Section */}
      <Grid item xs={12} md={8} sx={{ mt: 3 }}>
        <Typography variant="h3" fontWeight="700">
          Upcoming Events
        </Typography>
      </Grid>

      <Grid item xs={12} md={4} sx={{ mt: [0, 3] }}>
        <Button
          variant="contained"
          startIcon={<BCPCalendarDay />}
          fullWidth
          onClick={(e) => {
            setTab("events");
            navigate(
              `${organize ? "/organize" : ""}/organization/${
                organization?.id
              }?active_tab=events${embed ? "&embed=true" : ""}`,
              { replace: false }
            );
          }}
        >
          View All Events
        </Button>
      </Grid>

      <Grid item xs={12}>
        {loading || upcomingEvents?.length ? (
          <ListResponsive
            name="Upcoming Events"
            originalList={loading ? null : upcomingEvents ? upcomingEvents : []}
            doPages
            initialRowsPerPage={3}
            rowsPerPageArray={[3]}
            centerNav
            rowProps={{
              mapFunction: (row, index) => (
                <Grid key={index} item xs={12} md={4} sx={{ p: 1 }}>
                  <EventCard
                    event={row}
                    key={row?.id || index}
                    organize={organize}
                    authContext={authContext}
                    deleteEvent={deleteEvent}
                    preventImageFetch
                  />
                </Grid>
              )
            }}
          />
        ) : !loading && !upcomingEvents?.length ? (
          <InfoContainer
            title="No upcoming events."
            body={
              organize
                ? "There are currently no upcoming events. Create an event to get started!"
                : "There are currently no upcoming events. Check back later or search for more!"
            }
            ActionButton={
              <Button
                variant="contained"
                onClick={(e) => {
                  setTab("events");

                  navigate(
                    organize
                      ? `/organize/event?orgId=${organization?.id}`
                      : `/organization/${organization?.id}?active_tab=events${embed ? "&embed=true" : ""}`
                  );
                }}
                startIcon={organize ? <BCPPlus /> : <BCPSearch />}
              >
                {organize ? "Create Event" : "View All Events"}
              </Button>
            }
            sx={{ mt: 1 }}
          />
        ) : (
          <></>
        )}
      </Grid>

      {/* Rankings Section */}
      <Grid item xs={12} md={8} sx={{ mt: 3 }}>
        <Typography variant="h3" fontWeight="700">
          Leaderboards
        </Typography>
      </Grid>

      <Grid item xs={12} md={4} sx={{ mt: [0, 3] }}>
        <Button
          variant="contained"
          startIcon={<BCPChartMixed />}
          fullWidth
          onClick={(e) => {
            setTab("leaderboards");
            navigate(
              `${organize ? "/organize" : ""}/organization/${
                organization?.id
              }?active_tab=leaderboards`,
              { replace: false }
            );
          }}
        >
          View Leaderboards
        </Button>
      </Grid>

      <Grid item xs={12}>
        {loading || leaderboardPreview?.length ? (
          <ListResponsive
            name="Leaderboards"
            originalList={
              loading ? null : leaderboardPreview ? leaderboardPreview : []
            }
            rowProps={{
              mapFunction: (row, index) => (
                <Grid key={index} item xs={12} md={4} sx={{ p: 1 }}>
                  <RankingCard row={row} theme={theme} organize={organize} />
                </Grid>
              )
            }}
          />
        ) : !loading && !leaderboardPreview?.length ? (
          <InfoContainer
            title="No leaderboards."
            body={
              organize
                ? "There are currently no leaderboards. Create an event or circuit to get started!"
                : "There are currently no leaderboards. Check back later!"
            }
            ActionButton={
              <Button
                variant="contained"
                onClick={(e) => {
                  if (organize) {
                    navigate(`/organize/event?orgId=${organization?.id}`);
                  } else {
                    // Use interceptExternalLink for external navigation to rankings
                    interceptExternalLink(`/rankings`, "_blank", () => {
                      window.location.href = `/rankings`;
                    });
                  }
                }}
                startIcon={organize ? <BCPPlus /> : <BCPChartMixed />}
              >
                {organize ? "Create Event" : "View My Leaderboards"}
              </Button>
            }
            sx={{ mt: 1 }}
          />
        ) : (
          <></>
        )}
      </Grid>

      <ContactOrganizationModal
        open={contactOrganizationModal}
        setOpen={setContactOrganizationModal}
        authContext={authContext}
        organization={organization}
      />
      <EmbeddedExitConfirmDialog
        open={dialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        brandName={brandName}
        storagePrefix={storagePrefix}
      />
    </Grid>
  );
}

export function RankingCard(props) {
  const { row, theme, organize } = props;

  const [photo, setPhoto] = useState(null);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        if (row?.league?.photoFileId) {
          const apiPhoto = await getData(`/files/${row.league.photoFileId}`);
          if (apiPhoto) {
            setPhoto(apiPhoto);
          }
        }
      } catch {}
    };
    fetchPhoto();
  }, [row?.league?.photoFileId]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
      }}
    >
      <ExternalCardActionArea
        href={`/rankings/${row?.league?.id}`}
        target="_blank"
      >
        <CardMedia
          component="img"
          image={
            row?.league?.itc && row?.league?.teams
              ? "https://www.frontlinegaming.org/wp-content/uploads/2015/03/clubs-300x201.jpg"
              : row?.league.photo?.url ||
                photo?.url ||
                row?.league.imageUrl ||
                row?.league?.gameSystem?.url ||
                `${getEnvironmentVariable(
                  "SYSTEM_IMAGES_CLOUDFRONT"
                )}/images/EventImage${
                  theme?.palette?.mode === "dark" ? "Dark" : ""
                }.png`
          }
          alt={row?.league?.name}
          sx={{
            backgroundColor: "#222222",
            maxHeight: "calc((100vw - 24px) * 0.5625)",
            "@media (min-width: 600px)": {
              maxHeight: "calc((100vw - 24px) * 0.5 * 0.5625)"
            },
            "@media (min-width: 900px)": {
              maxHeight: "calc((100vw - 24px) * 0.3333 * 0.5625)"
            },
            "@media (min-width: 1218px)": {
              maxHeight: "215px"
            }
          }}
        />

        <CardHeader
          title={
            <>
              <Typography
                variant="h4"
                s
                sx={{
                  m: 0,
                  fontWeight: "700",
                  fontSize: "20px",
                  lineHeight: "1.334"
                }}
              >
                {row?.league?.name}
              </Typography>
              <Typography
                gutterBottom={row?.league?.active}
                sx={{
                  fontWeight: "400",
                  fontSize: "18px",
                  lineHeight: "1.334"
                }}
              >
                {row?.league?.gameSystem?.name}
              </Typography>
              {!row?.league?.active && (
                <Typography
                  gutterBottom
                  sx={{
                    fontWeight: "400",
                    fontSize: "18px",
                    lineHeight: "1.334",
                    color: "error.main"
                  }}
                >
                  {`(Inactive)`}
                </Typography>
              )}
            </>
          }
          action={
            organize ? (
              <IconButton
                aria-label="options"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setOptionsAnchorEl(e.currentTarget);
                }}
              >
                <BCPEllipsisVertical />
              </IconButton>
            ) : null
          }
        />
        {row?.league && organize ? (
          <OptionsMenu
            anchorEl={optionsAnchorEl}
            setAnchorEl={setOptionsAnchorEl}
            circuit={row?.league}
          />
        ) : (
          <></>
        )}
      </ExternalCardActionArea>

      <CardContent>
        <Grid container spacing={1}>
          {/* Loop through rankings here */}
          {!row?.league?.hidePlacings && row?.leaderboards?.length ? (
            row?.leaderboards?.map((rank, index) => {
              if (index >= 5) return <></>;

              return (
                <>
                  <Grid item xs={2}>
                    <Typography> {rank?.placing}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography>
                      {userDisplayName(
                        rank?.user?.firstName,
                        rank?.user?.lastName
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography align="right">
                      {Math.round((rank?.totalPoints + Number.EPSILON) * 100) /
                        100}
                    </Typography>
                  </Grid>
                </>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography>No rankings yet</Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <ExternalCardActionArea
        href={`/rankings/${row?.league?.id}`}
        target="_blank"
      >
        <CardActions>
          <Button
            fullWidth
            sx={{
              color: "link.main",
              textTransform: "none"
            }}
          >
            View complete leaderboard...
          </Button>
        </CardActions>
      </ExternalCardActionArea>
    </Card>
  );
}

function OptionsMenu(props) {
  const { anchorEl, setAnchorEl, circuit } = props;
  var authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const open = Boolean(anchorEl);
  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={async (e) => {
            setExportModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Export Rankings
        </MenuItem>
      </StyledMenu>
      {loading && <ModalLoadingFull />}
      {exportModal && (
        <ExportModal
          label="Export Circuit Player Placings"
          exportTypes={["placings"]}
          circuit={circuit}
          setLoading={setLoading}
          open={exportModal}
          setOpen={setExportModal}
          authContext={authContext}
        />
      )}
    </>
  );
}
